import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { indexOf } from 'lodash';
import { ButtonIcon, Tabs } from '@statsbomb/kitbag-components';
import {
  TabWrapper,
  StatCategoryTabsShowAll,
} from './StatCatgegoryTabs.styles';
import {
  buildScoutTabs,
  getAllowedChildren,
  getAllowedTabLabels,
  resolveCategoryName,
} from './StatCategoryTabs.helpers';
import { useStore } from '../../../../../store/useStore';
import { useGetPlayerStatsCategoryDefinitions } from '../../../../../utils/hooks/useGetPlayerStatCategories';
import { getCategories } from '../ScoutPlayers.dataManipulation';
import { BIO_CATEGORY } from '../ScoutPlayers.constants';

const StatCategoryTabs = ({
  __storybookStatCategories = null,
  __storybookSelectedCategoryName = null,
  __storybookSelectedSubCategoryName = null,
}) => {
  const { categoryDefinitions: apiStatCategories } =
    useGetPlayerStatsCategoryDefinitions();
  const statCategories =
    __storybookStatCategories || getCategories(apiStatCategories);

  const selectedCategoryName =
    __storybookSelectedCategoryName ||
    useStore((s) => s.scoutSelectedCategoryName);
  const setSelectedCategoryName = useStore(
    (s) => s.setScoutSelectedCategoryName
  );
  const selectedSubCategoryName =
    __storybookSelectedSubCategoryName ||
    useStore((s) => s.scoutSelectedSubCategoryName);
  const setSelectedSubCategoryName = useStore(
    (s) => s.setScoutSelectedSubCategoryName
  );
  const selectedPositions = useStore((s) => s.selectedPositions);

  const prevCategoryName = useRef(selectedCategoryName);
  const prevSubCategoryName = useRef(selectedSubCategoryName);

  const [showCondensedTabs, setShowCondensedTabs] = useState(true);
  const allowedTabLabels = getAllowedTabLabels(selectedPositions);
  const allowedChildren = getAllowedChildren(selectedPositions);
  const tabs = buildScoutTabs(
    statCategories,
    allowedTabLabels,
    allowedChildren,
    showCondensedTabs
  );
  const statCategoryNames = tabs.map((c) => c.label);
  const activeTableIndexRaw = indexOf(statCategoryNames, selectedCategoryName);
  const activeTableIndex = activeTableIndexRaw < 0 ? 0 : activeTableIndexRaw;
  const activeSubCategoryIndexRaw = tabs[activeTableIndex]?.children.findIndex(
    (c) => c.label === selectedSubCategoryName
  );
  const activeSubCategoryIndex =
    activeSubCategoryIndexRaw < 0 ? 0 : activeSubCategoryIndexRaw;
  const handleTabChange = (_, tab) => {
    const tabIndex = parseInt(tab.value, 10);
    const newCatName = statCategoryNames[tabIndex];
    const newStatCategory = statCategories.find((s) => s.name === newCatName);
    setSelectedCategoryName(newStatCategory.name);
    setSelectedSubCategoryName(newStatCategory.subcategories[0].name);
  };

  // Reset to Bio category when position changes and current selection becomes invalid
  useEffect(() => {
    // Carry a persisted 'Offensive Line' selection over to 'Blocking' (or vice
    // versa) rather than resetting it to Bio
    const resolvedCategoryName = resolveCategoryName(
      selectedCategoryName,
      statCategoryNames
    );
    if (resolvedCategoryName !== selectedCategoryName) {
      setSelectedCategoryName(resolvedCategoryName);
      return;
    }

    // Check if current category/subcategory is valid for the current tabs
    const isCurrentCategoryValid =
      statCategoryNames.includes(selectedCategoryName);
    const currentCategory = tabs.find((t) => t.label === selectedCategoryName);
    const isCurrentSubCategoryValid =
      isCurrentCategoryValid &&
      currentCategory?.children?.some(
        (child) => child.label === selectedSubCategoryName
      );

    // Reset to Bio if current selection is invalid for the current position
    if (!isCurrentSubCategoryValid) {
      setSelectedCategoryName(BIO_CATEGORY.name);
      setSelectedSubCategoryName(BIO_CATEGORY.subcategories[0].name);
      prevCategoryName.current = BIO_CATEGORY.name;
      prevSubCategoryName.current = BIO_CATEGORY.subcategories[0].name;
    } else {
      // Update refs to current values
      prevCategoryName.current = selectedCategoryName;
      prevSubCategoryName.current = selectedSubCategoryName;
    }
  }, [
    selectedPositions,
    tabs,
    statCategoryNames,
    selectedCategoryName,
    selectedSubCategoryName,
    setSelectedCategoryName,
    setSelectedSubCategoryName,
  ]);

  return (
    <TabWrapper>
      <StatCategoryTabsShowAll>
        <ButtonIcon
          displayText="right"
          off={!showCondensedTabs}
          size="small"
          variant="ghost"
          shape="pill"
          icon="Visibility"
          onClick={() => setShowCondensedTabs(!showCondensedTabs)}
        >
          {showCondensedTabs ? 'Show all tabs' : 'Hide extra tabs'}
        </ButtonIcon>
      </StatCategoryTabsShowAll>
      <Tabs
        tabs={tabs}
        isParent
        activeTab={activeTableIndex}
        onTabChange={(e, tab) => handleTabChange(e, tab)}
      />
      <Tabs
        tabs={tabs[activeTableIndex]?.children}
        activeTab={activeSubCategoryIndex}
        onTabChange={(e, tab) => setSelectedSubCategoryName(tab.label)}
      />
    </TabWrapper>
  );
};

StatCategoryTabs.propTypes = {
  __storybookStatCategories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      subcategories: PropTypes.arrayOf(PropTypes.shape({})),
    })
  ),
  __storybookSelectedCategoryName: PropTypes.string,
  __storybookSelectedSubCategoryName: PropTypes.string,
};

export default StatCategoryTabs;
